import React, { useEffect, useState, useMemo, useRef } from "react";
import { Button, ButtonGroup, Container, ToggleButton } from "react-bootstrap";
import { Link } from "react-router-dom";
import DataTable from "react-data-table-component";
import FilterComponent from "../Common/FilterComponent";
import AppLoaderTable from "../AppLoader/AppLoaderTable";
import TooltipPositioned from "../Tooltip/TooltipPositioned";
import AgentAttendanceTimeline, { AgentTimelineData, buildTimelineCSVRows, csvSafe } from "./AgentAttendanceTimeline";
import { GetApiMethod, PostApiMethod } from "../../core/services/apiService";
import constant from "../../core/constant";
import { CheckPropertyExistOrNot, formatLocData, DisplayToaster } from "../../core/services/sharedService";
import { useAppSelector } from "../../core/redux/hooks";
import { DtoAgentStatus } from "../../core/dto/dashboard.dto";
import "./AgentAttendanceTimeline.css";

const Agentlist = () => {
    const [deleteMsg, setDeleteMsg] = useState<any>();
    const [deleteMsgtimeout, setDeleteMsgtimeout] = useState(false);
    const [filterText, setFilterText] = useState("");
    const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
    const [showLoader, setShowLoader] = useState(true);
    const [agentStatusListInfo, setAgentStatusListInfo] = React.useState<{ [index: string]: string }>({});
    const [data, setData] = useState<any>([]);
    const [statusFilter, setStatusFilter] = useState<string>("active");
    const [exporting, setExporting] = useState(false);
    const [showReportPopover, setShowReportPopover] = useState(false);
    const [selectedPreset, setSelectedPreset] = useState("today");
    const reportPopoverRef = useRef<HTMLDivElement>(null);

    const datePresets: { label: string; value: string; getDates: () => { from: string; to: string } }[] = [
        {
            label: "Today", value: "today",
            getDates: () => {
                const d = new Date().toLocaleDateString("en-CA");
                return { from: d, to: d };
            }
        },
        {
            label: "Yesterday", value: "yesterday",
            getDates: () => {
                const d = new Date(); d.setDate(d.getDate() - 1);
                const s = d.toLocaleDateString("en-CA");
                return { from: s, to: s };
            }
        },
        {
            label: "Last 1 Week", value: "1week",
            getDates: () => {
                const to = new Date();
                const from = new Date(); from.setDate(from.getDate() - 7);
                return { from: from.toLocaleDateString("en-CA"), to: to.toLocaleDateString("en-CA") };
            }
        },
        {
            label: "Last 2 Weeks", value: "2weeks",
            getDates: () => {
                const to = new Date();
                const from = new Date(); from.setDate(from.getDate() - 14);
                return { from: from.toLocaleDateString("en-CA"), to: to.toLocaleDateString("en-CA") };
            }
        },
        {
            label: "Last 3 Weeks", value: "3weeks",
            getDates: () => {
                const to = new Date();
                const from = new Date(); from.setDate(from.getDate() - 21);
                return { from: from.toLocaleDateString("en-CA"), to: to.toLocaleDateString("en-CA") };
            }
        },
        {
            label: "Last 1 Month", value: "1month",
            getDates: () => {
                const to = new Date();
                const from = new Date(); from.setMonth(from.getMonth() - 1);
                return { from: from.toLocaleDateString("en-CA"), to: to.toLocaleDateString("en-CA") };
            }
        },
    ];

    const getSelectedDates = (): { from: string; to: string } => {
        const preset = datePresets.find(p => p.value === selectedPreset);
        return preset ? preset.getDates() : { from: new Date().toLocaleDateString("en-CA"), to: new Date().toLocaleDateString("en-CA") };
    };

    const getDisplayDateRange = (): string => {
        const { from, to } = getSelectedDates();
        if (!from || !to) return "";
        const fmt = (s: string) => {
            const d = new Date(s + "T00:00:00");
            return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
        };
        if (from === to) return fmt(from);
        const fmtShort = (s: string) => {
            const d = new Date(s + "T00:00:00");
            return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
        };
        const toDate = new Date(to + "T00:00:00");
        return `${fmtShort(from)} – ${fmtShort(to)}, ${toDate.getFullYear()}`;
    };

    // Close popover when clicking outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (reportPopoverRef.current && !reportPopoverRef.current.contains(e.target as Node)) {
                setShowReportPopover(false);
            }
        };
        if (showReportPopover) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [showReportPopover]);

    const statusOptions = [
        { name: "Active", value: "active" },
        { name: "Inactive", value: "inactive" },
        { name: "All", value: "all" },
    ];

    // allAgentStatusDetails
    let authState = useAppSelector((state) => state.auth);
    const locationUrl = "tenant/location-crud";
    const [agentMappedLocationDetails, setAgentMappedLocationDetails] = useState([{
        name: "",
        id: null
    }]);

    const getAgentList = React.useCallback(async () => {
        try {
            const res = await GetApiMethod("tenant/agent-crud");
            setData(res?.data?.data || []);
        } catch (err) {
            console.error("Failed to fetch agent list:", err);
            setData([]);
        } finally {
            setShowLoader(false);
        }
    }, []);



    useEffect(() => {
        getAgentList();
        GetApiMethod(locationUrl).then((res) => {
            let newArray = formatLocData(res.data.data);
            setAgentMappedLocationDetails((prevState) => [...newArray]);
        });
    }, [formatLocData, getAgentList]);

    React.useEffect(() => {
        const agentStatusList: { [index: string]: string } = {};
        if (authState.allAgentStatusDetails && authState.allAgentStatusDetails.length) {
            authState.allAgentStatusDetails.forEach((statusDetails: DtoAgentStatus) => {
                agentStatusList[statusDetails.email] = statusDetails.status;
            });
            setAgentStatusListInfo({ ...agentStatusList });
        }
    }, [authState.allAgentStatusDetails])

    const getLocationsAsCSV = (data: any) => {
        if (!Array.isArray(data)) return "";
        return data
            .map((item: any) => {
                return item?.name || "";
            })
            .join(",");
    };

    const primaryAndSecondaryLocations = (priLocation: string, secLocations: string) => {
        const pri = priLocation ? priLocation.split(",").filter(Boolean) : [];
        const sec = secLocations ? secLocations.split(",").filter(Boolean) : [];
        return [...pri, ...sec];
    }

    const columns: any = [
        {
            name: "Name",
            cell: (row: any) => (
                <div className="agent-status-table-list text-capitalize">
                    <span
                        className={
                            agentStatusListInfo[row.email] ?
                                agentStatusListInfo[row.email].toLowerCase() :
                                'offline'
                        }
                    ></span>
                    <span className="status-text">
                        {
                            row.name
                        }
                    </span>
                </div>
            ),
            selector: (row: any) => (CheckPropertyExistOrNot(row, 'name')),
            sortable: true,
        },
        {
            name: "Email",
            selector: (row: any) => row['email'],
            cell: (row: any) => (
                <div>
                    {TooltipPositioned({ 'placement': 'top', 'item': CheckPropertyExistOrNot(row, 'email') })}
                </div>
            ),
            sortable: true,
            style: {
                lineHeight: '1.4',
                paddingBottom: '3px',
                paddingTop: '3px'
            }
        },
        {
            name: "Display Name",
            selector: (row: any) => (CheckPropertyExistOrNot(row, 'display_name')),
            sortable: true,
        },
        {
            name: "Status",
            selector: (row: any) => ('isActive' in row ? (row.isActive ? "Active" : "Inactive") : "-"),
            sortable: true,
        },
        {
            name: "Primary Locations",
            selector: (row: any) => (
                <div>
                    {('primary_location' in row && row.primary_location.length > 0) ?
                        TooltipPositioned({
                            placement: "top",
                            item: getLocationsAsCSV(row.primary_location),
                        }) : "-"}
                </div>
            ),
        },
        {
            name: "Secondary Locations",
            selector: (row: any) => (
                <div>
                    {('secondary_location' in row && row.secondary_location.length > 0) ?
                        TooltipPositioned({
                            placement: "top",
                            item: getLocationsAsCSV(row.secondary_location),
                        }) : "-"}
                </div>
            ),
        },
        {
            name: "Actions",
            cell: (row: any) => (
                <div>
                    <Link
                        to={constant.AGENT_EDIT}
                        state={{
                            id: row._id,
                            names: row.name,
                            emails: row.email,
                            display_names: row.display_name,
                            is_active: row.isActive,
                            agentMappedLocationDetails: agentMappedLocationDetails,
                            assignAllLocs: row.assignAllLocs,
                            mappedLocations: row.mappedLocations,
                            hiddenLocations: row.hiddenLocations,
                            timeZone: row.timeZone,
                            // primaryLocations: getLocationsAsCSV(row.primary_location) + "," + getLocationsAsCSV(row.secondary_location)
                            primaryLocations: primaryAndSecondaryLocations(getLocationsAsCSV(row.primary_location), getLocationsAsCSV(row.secondary_location))
                        }}
                    >
                        <i
                            className="bi bi-pencil-square text-primary m-1 app-link-color"
                            style={{ fontSize: 24 }}
                        ></i>
                    </Link>
                    {/* <i
                        className="bi bi-trash-fill text-danger m-1 hover cursor-pointer"
                        onClick={(e) => {
                            handleDelete(e, row._id, row.name);
                        }}
                        style={{ fontSize: 24 }}
                    ></i> */}
                </div>
            ),
        },
    ];

    const subHeaderComponent = useMemo(() => {
        const handleClear = () => {
            if (filterText) {
                setResetPaginationToggle(!resetPaginationToggle);
                setFilterText("");
            }
        };

        return (
            <div className="status-filter-wrapper">
                <ButtonGroup className="status-filter-group">
                    {statusOptions.map((option) => (
                        <ToggleButton
                            key={option.value}
                            id={`agent-status-filter-${option.value}`}
                            type="radio"
                            name="agentStatusFilter"
                            value={option.value}
                            checked={statusFilter === option.value}
                            onChange={(e) => setStatusFilter(e.currentTarget.value)}
                            className="status-filter-btn"
                        >
                            {option.name}
                        </ToggleButton>
                    ))}
                </ButtonGroup>
                <FilterComponent
                    onFilter={(e: React.ChangeEvent<HTMLInputElement>) => setFilterText(e.target.value)}
                    onClear={handleClear}
                    filterText={filterText}
                />
            </div>
        );
    }, [filterText, resetPaginationToggle, statusFilter]);

    const filteredItems = (data || []).filter((item: any) =>
            statusFilter === "all" ? true
                : statusFilter === "active" ? item.isActive === true
                : item.isActive === false
        ).filter(
            (item: any) =>
                JSON.stringify([
                    item.name || "", item.email || "", item.display_name || "",
                    (item.primary_location || []).map((loc: any) => (loc.name)),
                    (item.secondary_location || []).map((loc: any) => (loc.name)),
                    (item.isActive ? "active" : "Inactive")
                ])
                    .toLowerCase()
                    .indexOf(filterText.toLowerCase()) !== -1
        );

    const exportAllTimelines = React.useCallback(async () => {
        if (!filteredItems || filteredItems?.length === 0) return;
        setExporting(true);
        setShowReportPopover(false);
        try {
            const { from: fromDate, to: toDate } = getSelectedDates();

            // Collect all valid agent IDs and names
            const agentMap: { [id: string]: string } = {};
            for (const agent of filteredItems) {
                if (agent._id) {
                    agentMap[agent._id] = agent.name || agent.display_name || agent.email || "Unknown";
                }
            }
            const agentIds = Object.keys(agentMap);

            const allRows: string[][] = [];
            allRows.push(["Date", "Agent Name", "Status", "Start Time", "End Time", "Duration", "Hrs Worked"]);

            if (agentIds.length === 0) {
                allRows.push(["", "No agents with valid IDs", "", "", "", "", ""]);
            } else {
                // Single API call with all agent IDs
                const res = await PostApiMethod("admin/dashboard-chart/agent_activity_reports", {
                    from_date: fromDate,
                    to_date: toDate,
                    clientTZ: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    include_segments: true,
                    agent_id: agentIds,
                });

                // Build date-first structure: { dateStr -> [{ agentName, rows }] }
                const dateAgentMap: { [date: string]: { agentName: string; rows: string[][] }[] } = {};

                if (res.data?.status === "Success" && res.data.data) {
                    for (const agent of filteredItems) {
                        if (!agent._id) continue;
                        const agentData: AgentTimelineData = res.data.data[agent._id];
                        if (!agentData) continue;

                        const hasSegments = agentData.segments && agentData.segments.length > 0;
                        const hasAggDays = agentData.agg_days && agentData.agg_days.length > 0;
                        if (!hasSegments && !hasAggDays) continue;

                        const name = agent.name || agent.display_name || agent.email || "Unknown";
                        const agentRows = buildTimelineCSVRows(name, agentData);

                        // Group this agent's rows by date (column index 2)
                        // and reorder columns in a single pass: [Date, AgentName, Status, Start, End, Duration, HrsWorked]
                        const rowsByDate: { [date: string]: string[][] } = {};
                        for (const r of agentRows) {
                            const dateVal = r[2];
                            if (!dateVal) continue;
                            if (!rowsByDate[dateVal]) rowsByDate[dateVal] = [];
                            const dateRows = rowsByDate[dateVal];
                            const isFirst = dateRows.length === 0;
                            dateRows.push([
                                "",                                     // Date — shown in header row
                                isFirst ? csvSafe(name) : "",           // Agent name on first row only
                                r[1], r[3], r[4], r[5], r[6]          // Status, Start, End, Duration, HrsWorked
                            ]);
                        }

                        // Merge into the date-first map
                        const dateKeys = Object.keys(rowsByDate);
                        for (let d = 0; d < dateKeys.length; d++) {
                            const dateVal = dateKeys[d];
                            if (!dateAgentMap[dateVal]) dateAgentMap[dateVal] = [];
                            dateAgentMap[dateVal].push({ agentName: name, rows: rowsByDate[dateVal] });
                        }
                    }
                }

                // Sort dates ascending and output date-first, agents within each date
                const sortedDates = Object.keys(dateAgentMap).sort((a, b) =>
                    new Date(a).getTime() - new Date(b).getTime()
                );

                if (sortedDates.length === 0) {
                    allRows.push(["", "", "No agents activity found", "", "", "", ""]);
                } else {
                    const emptyRow = ["", "", "", "", "", "", ""];
                    let isFirstDate = true;
                    for (const dateStr of sortedDates) {
                        const agents = dateAgentMap[dateStr];
                        if (!isFirstDate) allRows.push(emptyRow);
                        isFirstDate = false;

                        // Date header row
                        allRows.push([dateStr, "", "", "", "", "", ""]);

                        // Push each agent's rows without spread — safe for any array size
                        for (const { rows } of agents) {
                            for (let i = 0; i < rows.length; i++) {
                                allRows.push(rows[i]);
                            }
                        }
                    }
                }
            }

            // Build CSV parts array for Blob — avoids creating one massive concatenated string.
            // Blob internally handles concatenation more efficiently than a single .join("\n").
            const csvParts: string[] = [];
            for (let i = 0; i < allRows.length; i++) {
                if (i > 0) csvParts.push("\n");
                csvParts.push(allRows[i].join(","));
            }
            const blob = new Blob(csvParts, { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.setAttribute("href", url);
            const fileLabel = fromDate === toDate ? fromDate : `${fromDate}_to_${toDate}`;
            link.setAttribute("download", `All-Agents-Timeline-${fileLabel}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            DisplayToaster("bg-success", "Timeline data exported successfully!");
        } catch (error) {
            console.error("Export error:", error);
            DisplayToaster("bg-danger", "Failed to export timeline data.");
        } finally {
            setExporting(false);
        }
    }, [filteredItems, selectedPreset]);

    const ExpandedComponent = React.useCallback(({ data: rowData }: any) => {
        const liveStatus = agentStatusListInfo[rowData.email] || "offline";
        return <AgentAttendanceTimeline agentId={rowData._id} liveStatus={liveStatus} agentName={rowData.name} />;
    }, [agentStatusListInfo]);

    return (
        <Container fluid>
            <div className="align-items-center d-flex mt-3">
                <div className="col-sm-6">
                    <h2 className="heading-font head-heading"><span className="header_underline">Agents</span></h2>
                </div>
                <div className="col-sm-6 d-flex justify-content-end align-items-center gap-2">
                    <div ref={reportPopoverRef} style={{ position: "relative" }}>
                        <Button
                            className="buttons app-button-color"
                            style={{ background: "#0187c4" }}
                            onClick={() => setShowReportPopover(!showReportPopover)}
                            disabled={exporting || !filteredItems || filteredItems.length === 0}
                        >
                            {exporting && (
                                <span
                                    className="spinner-border text-white me-1"
                                    style={{ width: "16px", height: "16px" }}
                                ></span>
                            )}
                            <i className="bi bi-download" aria-hidden="true"></i> Timeline Report
                        </Button>
                        {showReportPopover && (
                            <div className="timeline-report-popover">
                                <h6 className="timeline-report-popover-title">
                                    <i className="bi bi-calendar3" style={{ color: "#0187c4" }}></i> Download Timeline Report
                                </h6>
                                <label className="timeline-report-popover-label">Quick Select</label>
                                <div className="timeline-report-preset-chips">
                                    {datePresets.map((preset) => (
                                        <span
                                            key={preset.value}
                                            className={`timeline-report-chip${selectedPreset === preset.value ? " active" : ""}`}
                                            onClick={() => setSelectedPreset(preset.value)}
                                        >
                                            {preset.label}
                                        </span>
                                    ))}
                                </div>
                                <div className="timeline-report-popover-footer">
                                    <span className="timeline-report-date-display">
                                        <i className="bi bi-calendar-check" style={{ color: "#0187c4" }}></i>{" "}
                                        {getDisplayDateRange()}
                                    </span>
                                    <Button
                                        size="sm"
                                        className="app-button-color"
                                        style={{ background: "#0187c4", borderRadius: "6px", padding: "6px 20px" }}
                                        onClick={exportAllTimelines}
                                        disabled={exporting}
                                    >
                                        {exporting ? (
                                            <span className="spinner-border text-white me-1" style={{ width: "14px", height: "14px" }}></span>
                                        ) : (
                                            <i className="bi bi-download"></i>
                                        )}{" "}
                                        Download CSV
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                    <Link to={constant.AGENT_CREATE} className="text-white link-text">
                        <Button className="buttons app-button-color" id="agent-create" style={{ background: "#0187c4" }}>
                            <i className="bi bi-plus-lg" aria-hidden="true"></i> Add
                        </Button>
                    </Link>
                </div>
            </div>
            <div className="agent-attendance-table">
                <Container fluid>
                    <div className="text-center text-success">
                        {deleteMsgtimeout && (deleteMsg && <b>{deleteMsg}</b>)}
                    </div>
                    <DataTable
                        progressPending={showLoader}
                        progressComponent={<AppLoaderTable />}
                        columns={columns}
                        data={filteredItems}
                        pagination
                        paginationRowsPerPageOptions={[5, 10, 15, 20]}
                        highlightOnHover
                        subHeader
                        persistTableHead
                        subHeaderComponent={subHeaderComponent}
                        expandableRows={true}
                        expandableRowsComponent={ExpandedComponent}
                        expandOnRowClicked
                    />
                </Container>
            </div>
        </Container>
    );
};

export default Agentlist;
